import { useEffect, useState } from "react";
import { Bell, Send, Loader2, Users, RefreshCw, Smartphone, Globe, History, Repeat, Trash2, BookOpenText } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";

export default function AdminNotifications() {
  const { t, lang } = useI18n();
  // Bilingual broadcast: paired ES/EN inputs. Either language can be left
  // blank — the backend falls back to the other for subs in that language.
  const [titleEs, setTitleEs] = useState("");
  const [bodyEs, setBodyEs] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [sending, setSending] = useState(false);
  const [stats, setStats] = useState({ count: 0, device_count: 0, member_count: 0, by_language: {}, items: [] });
  const [loadingStats, setLoadingStats] = useState(true);
  const [filter, setFilter] = useState("");

  // Past broadcasts (for the "Re-send" flow).
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [resendingId, setResendingId] = useState("");
  const [confirmResend, setConfirmResend] = useState(null); // broadcast doc
  const [confirmDelete, setConfirmDelete] = useState(null); // broadcast doc

  // Test verse push (instant iOS delivery diagnostic).
  const [testingVerse, setTestingVerse] = useState(false);

  const reload = async () => {
    setLoadingStats(true);
    try {
      const { data } = await api.get("/admin/push/subscriptions");
      setStats(data);
    } catch (err) {
      console.error("AdminNotifications: failed to load subscribers", err);
    } finally {
      setLoadingStats(false);
    }
  };

  const reloadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/admin/broadcasts");
      setHistory(data?.items || []);
    } catch (err) {
      console.error("AdminNotifications: failed to load history", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { reload(); reloadHistory(); }, []);

  const esCount = stats?.by_language?.es || 0;
  const enCount = stats?.by_language?.en || 0;
  const hasEs = !!(titleEs.trim() && bodyEs.trim());
  const hasEn = !!(titleEn.trim() && bodyEn.trim());

  const onSend = async () => {
    if (!hasEs && !hasEn) {
      toast.error(lang === "es"
        ? "Escribe titulo y mensaje en al menos un idioma."
        : "Write a title and message in at least one language.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", {
        title_es: hasEs ? titleEs.trim() : undefined,
        body_es: hasEs ? bodyEs.trim() : undefined,
        title_en: hasEn ? titleEn.trim() : undefined,
        body_en: hasEn ? bodyEn.trim() : undefined,
      });
      const es = data?.by_language?.es || 0;
      const en = data?.by_language?.en || 0;
      const parts = [];
      if (es) parts.push(lang === "es" ? `${es} en ES` : `${es} in ES`);
      if (en) parts.push(lang === "es" ? `${en} en EN` : `${en} in EN`);
      const breakdown = parts.length ? ` (${parts.join(" \u00b7 ")})` : "";
      toast.success(lang === "es"
        ? `Enviadas: ${data.sent}${breakdown} \u00b7 Fallidas: ${data.failed}`
        : `Sent: ${data.sent}${breakdown} \u00b7 Failed: ${data.failed}`);
      setTitleEs(""); setBodyEs(""); setTitleEn(""); setBodyEn("");
      reloadHistory();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally { setSending(false); }
  };

  const onResend = async (b) => {
    if (!b?.id) return;
    setResendingId(b.id);
    try {
      const { data } = await api.post(`/admin/broadcasts/${b.id}/resend`);
      const es = data?.by_language?.es || 0;
      const en = data?.by_language?.en || 0;
      const parts = [];
      if (es) parts.push(lang === "es" ? `${es} en ES` : `${es} in ES`);
      if (en) parts.push(lang === "es" ? `${en} en EN` : `${en} in EN`);
      const breakdown = parts.length ? ` (${parts.join(" \u00b7 ")})` : "";
      toast.success(lang === "es"
        ? `Reenviado a ${data.sent}${breakdown} \u00b7 Fallidas: ${data.failed}`
        : `Resent to ${data.sent}${breakdown} \u00b7 Failed: ${data.failed}`);
      reloadHistory();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setResendingId("");
      setConfirmResend(null);
    }
  };

  const onDeleteHistory = async (b) => {
    if (!b?.id) return;
    try {
      await api.delete(`/admin/broadcasts/${b.id}`);
      toast.success(lang === "es" ? "Aviso eliminado" : "Broadcast deleted");
      reloadHistory();
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setConfirmDelete(null);
    }
  };

  const onSendTestVerse = async () => {
    setTestingVerse(true);
    try {
      const { data } = await api.post("/admin/push/test-verse");
      if (data?.skipped) {
        toast.info(lang === "es"
          ? "El versiculo de hoy ya se envio esta manana."
          : "Today's verse was already sent this morning.");
      } else {
        toast.success(lang === "es"
          ? `Versiculo de prueba enviado: ${data.sent || 0} entregados, ${data.failed || 0} fallidos, ${data.removed || 0} revocados`
          : `Test verse sent: ${data.sent || 0} delivered, ${data.failed || 0} failed, ${data.removed || 0} revoked`);
      }
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || t.common.error));
    } finally {
      setTestingVerse(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-notifications-page">
      <div>
        <h1 className="font-serif-display text-2xl font-semibold flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          {lang === "es" ? "Notificaciones push" : "Push notifications"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {lang === "es"
            ? "Envia avisos a los miembros que han activado notificaciones en su telefono."
            : "Send alerts to members who have enabled notifications on their phones."}
        </p>
      </div>

      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-5 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Users className="h-5 w-5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {lang === "es" ? "Personas suscritas" : "Subscribed members"}
            </p>
            <p className="font-serif-display text-xl font-semibold" data-testid="notif-member-count">
              {loadingStats ? "..." : (stats.member_count ?? stats.count ?? 0)}
            </p>
          </div>
          <Badge variant="secondary" className="rounded-full gap-1">
            <Smartphone className="h-3 w-3" />
            {loadingStats ? "..." : (stats.device_count ?? stats.count ?? 0)} {lang === "es" ? "dispositivos" : "devices"}
          </Badge>
        </CardContent>
      </Card>

      {/* Who has notifications enabled */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid="notif-subscribers-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="font-serif-display text-base font-semibold">
                {lang === "es" ? "Quien tiene notificaciones activadas" : "Who has notifications enabled"}
              </p>
              <p className="text-xs text-muted-foreground">
                {lang === "es"
                  ? "Personas que activaron el aviso desde su telefono o navegador."
                  : "Members who turned on push from their phone or browser."}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={reload}
              disabled={loadingStats}
              data-testid="notif-reload"
              aria-label="reload subscribers"
            >
              <RefreshCw className={`h-4 w-4 ${loadingStats ? "animate-spin" : ""}`} />
            </Button>
          </div>

          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={lang === "es" ? "Buscar por nombre o telefono..." : "Search by name or phone..."}
            className="h-10"
            data-testid="notif-search"
          />

          {loadingStats ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`sub-skel-${i}`} className="h-14 w-full rounded-xl" />
              ))}
            </div>
          ) : (stats.items || []).length === 0 ? (
            <div className="rounded-xl bg-muted/40 p-6 text-center text-sm text-muted-foreground" data-testid="notif-subscribers-empty">
              {lang === "es"
                ? "Nadie ha activado notificaciones aun. Los miembros pueden activarlas desde la pagina de Inicio."
                : "No one has enabled notifications yet. Members can turn them on from the Home page."}
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-xl bg-background ring-1 ring-black/5 overflow-hidden" data-testid="notif-subscribers-list">
              {(stats.items || [])
                .filter((s) => {
                  if (!filter.trim()) return true;
                  const f = filter.toLowerCase();
                  return (
                    (s.full_name || "").toLowerCase().includes(f)
                    || (s.phone || "").includes(filter.trim())
                  );
                })
                .map((s) => (
                  <li
                    key={s.member_id || `anon-${s.first_subscribed_at || Math.random()}`}
                    className="flex items-center gap-3 px-3 py-2.5"
                    data-testid={`notif-subscriber-${s.member_id || "anon"}`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                      {(s.full_name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">
                        {s.full_name || (lang === "es" ? "Sin nombre (anonimo)" : "Anonymous")}
                      </p>
                      <p className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        {s.phone && <span>{s.phone}</span>}
                        {s.language && (
                          <span className="inline-flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {(s.language || "").toUpperCase()}
                          </span>
                        )}
                        {s.first_subscribed_at && (
                          <span>
                            {lang === "es" ? "Desde" : "Since"} {fmtDateTime(s.first_subscribed_at, lang)}
                          </span>
                        )}
                      </p>
                    </div>
                    <Badge variant="outline" className="shrink-0 gap-1 text-[10px]">
                      <Smartphone className="h-3 w-3" />
                      {s.device_count} {s.device_count === 1
                        ? (lang === "es" ? "disp." : "dev.")
                        : (lang === "es" ? "disp." : "devs")}
                    </Badge>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div>
            <p className="font-serif-display text-base font-semibold">
              {lang === "es" ? "Nuevo aviso" : "New broadcast"}
            </p>
            <p className="text-xs text-muted-foreground">
              {lang === "es"
                ? `Cada persona recibe el mensaje en su idioma. Ahora mismo: ${esCount} en ES \u00b7 ${enCount} en EN. Deja un idioma vacio para usar el otro como respaldo.`
                : `Each person receives the message in their language. Right now: ${esCount} in ES \u00b7 ${enCount} in EN. Leave one language blank to fall back to the other.`}
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              {lang === "es"
                ? "Sin limite de caracteres. Si el aviso no cabe en la notificacion, al tocarla se abre el texto completo dentro de la app."
                : "No character limit. If the notification is truncated, tapping it opens the full announcement inside the app."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ---- Spanish column ---- */}
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full text-[10px]">ES</Badge>
                <p className="text-xs font-semibold">
                  {lang === "es" ? "Para quienes tienen el idioma en Espanol" : "For subscribers using Spanish"}
                </p>
                <span className="ml-auto text-[10px] text-muted-foreground">{esCount}</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {lang === "es" ? "Titulo (ES)" : "Title (ES)"}
                  <span className="text-muted-foreground"> ({titleEs.length})</span>
                </Label>
                <Input
                  value={titleEs}
                  onChange={(e) => setTitleEs(e.target.value)}
                  placeholder="Servicio de avivamiento esta noche"
                  data-testid="notif-title-es"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {lang === "es" ? "Mensaje (ES)" : "Message (ES)"}
                  <span className="text-muted-foreground"> ({bodyEs.length})</span>
                </Label>
                <Textarea
                  value={bodyEs}
                  rows={5}
                  onChange={(e) => setBodyEs(e.target.value)}
                  placeholder={lang === "es"
                    ? "Escribe todo el mensaje aqui. Sin limite de caracteres."
                    : "Write the full message here. No character limit."}
                  data-testid="notif-body-es"
                />
              </div>
            </div>

            {/* ---- English column ---- */}
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full text-[10px]">EN</Badge>
                <p className="text-xs font-semibold">
                  {lang === "es" ? "Para quienes tienen el idioma en Ingles" : "For subscribers using English"}
                </p>
                <span className="ml-auto text-[10px] text-muted-foreground">{enCount}</span>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {lang === "es" ? "Titulo (EN)" : "Title (EN)"}
                  <span className="text-muted-foreground"> ({titleEn.length})</span>
                </Label>
                <Input
                  value={titleEn}
                  onChange={(e) => setTitleEn(e.target.value)}
                  placeholder="Revival service tonight"
                  data-testid="notif-title-en"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {lang === "es" ? "Mensaje (EN)" : "Message (EN)"}
                  <span className="text-muted-foreground"> ({bodyEn.length})</span>
                </Label>
                <Textarea
                  value={bodyEn}
                  rows={5}
                  onChange={(e) => setBodyEn(e.target.value)}
                  placeholder={lang === "es"
                    ? "Escribe todo el mensaje aqui. Sin limite de caracteres."
                    : "Write the full message here. No character limit."}
                  data-testid="notif-body-en"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={onSend}
              disabled={sending || stats.count === 0 || (!hasEs && !hasEn)}
              data-testid="notif-send"
            >
              {sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              {lang === "es"
                ? `Enviar a ${stats.count || 0} dispositivo${stats.count === 1 ? "" : "s"}`
                : `Send to ${stats.count || 0} device${stats.count === 1 ? "" : "s"}`}
            </Button>
          </div>

          {stats.count === 0 && !loadingStats && (
            <p className="text-xs text-muted-foreground">
              {lang === "es"
                ? "Aun no hay dispositivos suscritos. Los miembros pueden activar notificaciones desde la pagina de Inicio."
                : "No subscribed devices yet. Members can enable notifications from the Home page."}
            </p>
          )}
        </CardContent>
      </Card>

      {/* -------- Past broadcasts (re-send) -------- */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid="notif-history-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <p className="font-serif-display text-base font-semibold flex items-center gap-2">
                <History className="h-4 w-4 text-primary" />
                {lang === "es" ? "Avisos enviados" : "Past broadcasts"}
              </p>
              <p className="text-xs text-muted-foreground">
                {lang === "es"
                  ? "Vuelve a enviar cualquier aviso anterior sin escribirlo de nuevo."
                  : "Re-send any prior broadcast without retyping it."}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={reloadHistory}
              disabled={loadingHistory}
              data-testid="notif-history-reload"
              aria-label="reload history"
            >
              <RefreshCw className={`h-4 w-4 ${loadingHistory ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {loadingHistory ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`hist-skel-${i}`} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : history.length === 0 ? (
            <div className="rounded-xl bg-muted/40 p-6 text-center text-sm text-muted-foreground" data-testid="notif-history-empty">
              {lang === "es"
                ? "Aun no has enviado ningun aviso."
                : "You have not sent any broadcasts yet."}
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-xl bg-background ring-1 ring-black/5 overflow-hidden" data-testid="notif-history-list">
              {history.map((b) => {
                const title = lang === "es"
                  ? (b.title_es || b.title_en || "")
                  : (b.title_en || b.title_es || "");
                const body = lang === "es"
                  ? (b.body_es || b.body_en || "")
                  : (b.body_en || b.body_es || "");
                const preview = body.length > 140 ? `${body.slice(0, 140)}\u2026` : body;
                return (
                  <li
                    key={b.id}
                    className="flex items-start gap-3 px-3 py-3"
                    data-testid={`notif-history-item-${b.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm truncate">{title || (lang === "es" ? "(Sin titulo)" : "(No title)")}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{preview}</p>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {b.created_at && (
                          <span>{lang === "es" ? "Creado" : "Created"} {fmtDateTime(b.created_at, lang)}</span>
                        )}
                        {b.last_sent_at && (
                          <span className="ml-2">
                            {"\u00b7 "}{lang === "es" ? "Ultimo envio" : "Last sent"} {fmtDateTime(b.last_sent_at, lang)}
                          </span>
                        )}
                        {typeof b.send_count === "number" && b.send_count > 0 && (
                          <span className="ml-2">{"\u00b7 "}{b.send_count}x</span>
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmResend(b)}
                        disabled={resendingId === b.id || stats.count === 0}
                        data-testid={`notif-history-resend-${b.id}`}
                      >
                        {resendingId === b.id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <Repeat className="h-4 w-4" />}
                        <span className="ml-1 hidden sm:inline">{lang === "es" ? "Reenviar" : "Re-send"}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirmDelete(b)}
                        aria-label="delete"
                        data-testid={`notif-history-delete-${b.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Confirm re-send */}
      <AlertDialog open={!!confirmResend} onOpenChange={(o) => { if (!o) setConfirmResend(null); }}>
        <AlertDialogContent data-testid="notif-history-resend-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lang === "es" ? "\u00bfReenviar este aviso?" : "Re-send this broadcast?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "es"
                ? `Se enviara a ${stats.count || 0} dispositivo${stats.count === 1 ? "" : "s"} con el mismo texto guardado.`
                : `It will be sent to ${stats.count || 0} device${stats.count === 1 ? "" : "s"} with the same stored text.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={() => onResend(confirmResend)}>
              {lang === "es" ? "Reenviar" : "Re-send"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete history entry */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="notif-history-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lang === "es" ? "\u00bfEliminar este aviso?" : "Delete this broadcast?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "es"
                ? "Se borra del historial. Las notificaciones ya enviadas no se pueden recuperar."
                : "It will be removed from the history. Notifications already delivered cannot be recalled."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDeleteHistory(confirmDelete)}>
              {lang === "es" ? "Eliminar" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="rounded-2xl bg-amber-50/60 border border-amber-200">
        <CardContent className="p-4 text-sm space-y-1">
          <p className="font-semibold text-amber-900">{lang === "es" ? "Nota sobre iPhone" : "Note about iPhone"}</p>
          <p className="text-amber-900">
            {lang === "es"
              ? "Los iPhones solo reciben notificaciones cuando el usuario ha instalado la app (Compartir \u2192 Anadir a inicio)."
              : "iPhones only receive notifications when the user has installed the app (Share \u2192 Add to Home Screen)."}
          </p>
        </CardContent>
      </Card>

      {/* -------- Diagnostic: send a real daily verse push right now -------- */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm" data-testid="notif-test-verse-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BookOpenText className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-serif-display text-base font-semibold">
                {lang === "es" ? "Probar el versiculo diario ahora" : "Test the daily verse now"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {lang === "es"
                  ? "Envia el versiculo de HOY a todos los dispositivos suscritos, ignorando el bloqueo diario. Sirve para verificar que las notificaciones llegan al iPhone sin esperar hasta las 8:00 AM."
                  : "Sends TODAY's verse to every subscribed device, ignoring the once-per-day lock. Use it to verify iPhone delivery without waiting until 8:00 AM."}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onSendTestVerse}
            disabled={testingVerse || stats.count === 0}
            data-testid="notif-test-verse-button"
          >
            {testingVerse
              ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              : <BookOpenText className="mr-1 h-4 w-4" />}
            {lang === "es"
              ? `Enviar versiculo de prueba a ${stats.count || 0} dispositivo${stats.count === 1 ? "" : "s"}`
              : `Send test verse to ${stats.count || 0} device${stats.count === 1 ? "" : "s"}`}
          </Button>
          {stats.count === 0 && !loadingStats && (
            <p className="text-[11px] text-muted-foreground">
              {lang === "es"
                ? "No hay dispositivos suscritos todavia."
                : "No subscribed devices yet."}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
